import React, { useState } from 'react';
import styles from './PrivacyPolicy.module.css';

export default function PrivacyPolicy({ onBack }) {
  const [activeTab, setActiveTab] = useState('readMore');

  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>

        {/* Top Bar */}
        <header className={styles.appBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={onBack}
            aria-label="Go Back"
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              stroke="#005CA9"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 8H5" />
              <polyline points="12 1 5 8 12 15" />
            </svg>
          </button>
        </header>

        {/* Scrollable Content */}
        <div className={styles.scrollContainer}>
          <main className={styles.contentSection}>

            {/* Brand */}
            <div className={styles.brandContainer}>
              <span className={styles.orangeBrand}>Bro</span>
              <span className={styles.blueBrand}>Bizee</span>
              <span className={styles.brandPartner}>up</span>
            </div>

            {/* Header */}
            <div className={styles.headingBlock}>
              <span className={styles.eyebrow}>PRIVACY & SECURITY</span>

              <h1 className={styles.pageTitle}>
                About BroBizee
                <br />
                <span>Privacy</span>
              </h1>

              <p className={styles.leadParagraph}>
                BroBizee is committed to protecting your privacy. This policy
                explains how we collect, use, and protect your personal data
                across our platform and services.
              </p>
            </div>

            {/* Policy Status Card */}
            <div className={styles.policyCard}>
              <div className={styles.policyIcon}>
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>

              <div className={styles.policyCardContent}>
                <strong>Your privacy matters</strong>
                <span>
                  Your information is handled with security and transparency.
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.btnGroup}>
              <button
                type="button"
                className={`${styles.ctaBtn} ${
                  activeTab === 'readMore'
                    ? styles.primaryBtn
                    : styles.secondaryBtn
                }`}
                onClick={() => setActiveTab('readMore')}
              >
                Read More
              </button>

              <button
                type="button"
                className={`${styles.ctaBtn} ${
                  activeTab === 'decline'
                    ? styles.primaryBtn
                    : styles.secondaryBtn
                }`}
                onClick={() => setActiveTab('decline')}
              >
                Decline
              </button>
            </div>

            {/* Welcome */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>01</div>

              <h2 className={styles.sectionTitle}>Welcome</h2>

              <p className={styles.bodyText}>
                Welcome to BroBizee. Your privacy matters to us. This page
                outlines how we collect and process your information when
                using our application and partner services.
              </p>

              <div className={styles.highlightBox}>
                <div className={styles.highlightDot}></div>

                <p>
                  We updated our Privacy Policy in accordance with new
                  standards and to ensure full security for our users and
                  partners.
                </p>
              </div>

              <h3 className={styles.subHeading}>
                Here's a summary of key points
              </h3>

              <ul className={styles.bulletList}>
                <li>
                  <span>01</span>
                  Data privacy and protection are at the core of our platform
                  architecture.
                </li>

                <li>
                  <span>02</span>
                  Clear controls are provided so you can manage your location
                  and personal information.
                </li>

                <li>
                  <span>03</span>
                  Please read the full Privacy Policy details to understand
                  your rights.
                </li>
              </ul>
            </section>

            {/* Introduction */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>02</div>

              <h2 className={styles.sectionTitle}>Introduction</h2>

              <p className={styles.bodyText}>
                This document describes how we collect, store, and process
                personal data when you use the BroBizee app, website, or
                associated services.
              </p>
            </section>

            {/* Data Collection */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>03</div>

              <h2 className={styles.sectionTitle}>
                Data Collection & Use
              </h2>

              <p className={styles.bodyText}>
                We collect information to provide better services to all
                users. The types of data collected depend on how you interact
                with our platform.
              </p>

              <div className={styles.numberedList}>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>01</span>
                  <div>
                    <strong>User Requirement</strong>
                    <p>
                      We require basic details like language and region to
                      customize the app interface.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>02</span>
                  <div>
                    <strong>Partner Registration</strong>
                    <p>
                      Partners provide phone number, service skills, current
                      address, and proof documents.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>03</span>
                  <div>
                    <strong>Connection Process</strong>
                    <p>
                      When connecting users to partners, location data may be
                      used for efficient service dispatch.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>04</span>
                  <div>
                    <strong>OTP Verification</strong>
                    <p>
                      Secure authentication takes place via One-Time
                      Passwords sent to registered mobile numbers.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>05</span>
                  <div>
                    <strong>Payment Process</strong>
                    <p>
                      Financial and wallet transactions are processed using
                      encrypted payment gateways.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>06</span>
                  <div>
                    <strong>Safety Standards</strong>
                    <p>
                      Interactions are monitored according to platform safety
                      standards for customers and partners.
                    </p>
                  </div>
                </div>

              </div>
            </section>

            {/* Security */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>04</div>

              <h2 className={styles.sectionTitle}>
                How we store your data safely
              </h2>

              <p className={styles.bodyText}>
                Your data is stored in secure cloud environments protected
                with standard firewall and encryption technologies.
              </p>

              <div className={styles.securityCard}>
                <div className={styles.securityIcon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="5" y="10" width="14" height="11" rx="2" />
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
                  </svg>
                </div>

                <div>
                  <strong>Secure Data Protection</strong>
                  <p>
                    Security measures are applied to help protect your
                    information from unauthorized access.
                  </p>
                </div>
              </div>

              <h3 className={styles.subHeading}>Data Deletion</h3>

              <p className={styles.bodyText}>
                You can request deletion of your account and related profile
                details anytime through the settings menu.
              </p>
            </section>

            {/* =========================================================
                NEW SECTION 05: Data Encryption & Transmission
                Required for Google Play "Data Safety" form compliance
            ========================================================= */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>05</div>

              <h2 className={styles.sectionTitle}>
                Data Encryption & Transmission
              </h2>

              <p className={styles.bodyText}>
                All user data collected by BroBizee is encrypted in transit
                using modern Transport Layer Security (TLS/HTTPS) protocols.
                This ensures that your personal information cannot be
                intercepted or read by unauthorized third parties during
                transmission.
              </p>

              <div className={styles.highlightBox}>
                <div className={styles.highlightDot}></div>
                <p>
                  Data is transmitted only over secure HTTPS connections. We do
                  not transmit any personal or sensitive user data over
                  unencrypted channels.
                </p>
              </div>
            </section>

            {/* =========================================================
                NEW SECTION 06: Data Retention & Deletion Policy
                Required for Google Play "Account Deletion" policy
            ========================================================= */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>06</div>

              <h2 className={styles.sectionTitle}>
                Data Retention & Deletion
              </h2>

              <p className={styles.bodyText}>
                We retain your personal data only for as long as necessary to
                provide our services and comply with legal obligations. When
                you request account deletion, we will delete or anonymize your
                data within a reasonable timeframe, unless we are legally
                required to retain certain information.
              </p>

              <h3 className={styles.subHeading}>
                How to Delete Your Account
              </h3>

              <p className={styles.bodyText}>
                If you have created an account with BroBizee, you can delete
                your account and associated data at any time. We provide two
                ways to request deletion:
              </p>

              <div className={styles.numberedList}>
                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>01</span>
                  <div>
                    <strong>In-App Deletion</strong>
                    <p>
                      Go to Settings → Account → Delete Account. This will
                      initiate the deletion of your account and all associated
                      personal data from our active systems.
                    </p>
                  </div>
                </div>

                <div className={styles.numberedItem}>
                  <span className={styles.itemNumber}>02</span>
                  <div>
                    <strong>Web Request</strong>
                    <p>
                      If you cannot access the app, you may request deletion by
                      visiting our dedicated account deletion page at
                      brobizee.com/delete-account or by contacting us at
                      support@brobizee.com. Please include your registered
                      phone number in your request.
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.securityCard}>
                <div className={styles.securityIcon}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </div>

                <div>
                  <strong>What Data is Deleted</strong>
                  <p>
                    When your account is deleted, we remove your profile
                    information, contact details, service history, and any
                    uploaded documents from our active databases.
                  </p>
                </div>
              </div>
            </section>

            {/* =========================================================
                NEW SECTION 07: Third-Party Data Sharing
                Required for Google Play "Data Safety" disclosure
            ========================================================= */}
            <section className={styles.sectionBlock}>
              <div className={styles.sectionNumber}>07</div>

              <h2 className={styles.sectionTitle}>
                Third-Party Data Sharing
              </h2>

              <p className={styles.bodyText}>
                BroBizee does not sell your personal information. We may share
                limited data with trusted service providers who help us operate
                the platform, such as:
              </p>

              <ul className={styles.bulletList}>
                <li>
                  <span>01</span>
                  Cloud hosting providers that store data securely on our
                  behalf.
                </li>
                <li>
                  <span>02</span>
                  Payment processors that handle financial transactions
                  securely.
                </li>
                <li>
                  <span>03</span>
                  Analytics providers that help us improve app performance
                  (using anonymized data only).
                </li>
              </ul>

              <p className={styles.bodyText}>
                These service providers are contractually obligated to protect
                your data and may only use it for the purposes we specify.
              </p>
            </section>

            {/* Bottom */}
            <div className={styles.bottomBrand}>
              <span>Bro</span>Bizee
            </div>

            <p className={styles.lastText}>
              Your privacy. Your control. Your trust.
            </p>

          </main>
        </div>

      </div>
    </div>
  );
}